/**
 * Wizard step 3: time window and weather.
 *
 * The weather condition is always a manual choice; the suggestion query only
 * pre-fills it. The suggestion is labelled as advisory on screen, because the
 * product rule is that a human answer always wins.
 */

import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'

import { WEATHER_CONDITIONS } from '@shared/types'
import { isValidLocalDateTime } from '@shared/validation'

import { WizardFooter } from '../../../components/layout/wizard'
import { Alert, AlertDescription, AlertTitle } from '../../../components/ui/alert'
import { Button } from '../../../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card'
import { Chip, ChipGroup } from '../../../components/ui/chip'
import { Field, FieldGroup } from '../../../components/ui/field'
import { Input, Label } from '../../../components/ui/input'
import { useWeatherSuggestion } from '../../../hooks/use-events'
import { currentLocale } from '../../../lib/i18n'
import { mutationErrorText } from '../../../lib/mutation-error'
import { useEventDraft } from '../../../stores/drafts'

export function NewEventWeatherPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { draft, update } = useEventDraft()
  const [startError, setStartError] = useState<string | null>(null)
  const [endError, setEndError] = useState<string | null>(null)

  const locale = currentLocale()
  // The endpoint takes the calendar date, not the start timestamp.
  const date = draft.eventDateStart.slice(0, 10)
  const suggestion = useWeatherSuggestion(draft.city, date, locale)
  const suggestionData = suggestion.data ?? null
  // Read as text rather than as an `ApiError`: a request that never reached the
  // server still has to say something instead of leaving the step silent.
  const suggestionError = mutationErrorText(suggestion.error, t)

  function goNext() {
    if (!isValidLocalDateTime(draft.eventDateStart)) {
      setStartError(t('eventNew.dateError'))
      setEndError(null)
      return
    }
    // The end has to describe a window: an earlier end would leave the engine with no
    // time constraint at all rather than with the mistake the organizer made. The
    // message belongs to the end field, because that is the value at fault.
    if (draft.eventDateEnd !== null && draft.eventDateEnd <= draft.eventDateStart) {
      setStartError(null)
      setEndError(t('eventNew.dateOrderError'))
      return
    }
    setStartError(null)
    setEndError(null)
    navigate('/me/events/new/review')
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('eventNew.stepWeather')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <FieldGroup>
          <Field
            label={t('eventNew.dateStartLabel')}
            htmlFor="draft-weather-start"
            required
            error={startError}
          >
            <Input
              id="draft-weather-start"
              type="datetime-local"
              value={draft.eventDateStart}
              onChange={(event) => {
                update({ eventDateStart: event.target.value })
                setStartError(null)
              }}
            />
          </Field>

          <Field label={t('eventNew.dateEndLabel')} htmlFor="draft-weather-end" error={endError}>
            <Input
              id="draft-weather-end"
              type="datetime-local"
              value={draft.eventDateEnd ?? ''}
              onChange={(event) => {
                update({ eventDateEnd: event.target.value.length > 0 ? event.target.value : null })
                setEndError(null)
              }}
            />
          </Field>

          <div className="flex flex-col gap-2">
            <Label>{t('eventNew.weatherLabel')}</Label>
            <ChipGroup label={t('eventNew.weatherLabel')}>
              {WEATHER_CONDITIONS.map((condition) => (
                <Chip
                  key={condition}
                  selected={draft.weatherCondition === condition}
                  onClick={() => update({ weatherCondition: condition })}
                >
                  {t(`weather.${condition}`)}
                </Chip>
              ))}
            </ChipGroup>
            <p className="text-muted-foreground text-xs">{t('eventNew.weatherHint')}</p>
          </div>
        </FieldGroup>

        {suggestion.isPending && date.length > 0 ? (
          <p className="text-muted-foreground text-xs">{t('common.loading')}</p>
        ) : null}

        {suggestionData && suggestionData.condition !== 'unknown' ? (
          <Alert variant="info">
            <AlertTitle>{t('eventNew.weatherSuggestionTitle')}</AlertTitle>
            <AlertDescription>
              {t(`weather.${suggestionData.condition}`)}
              {suggestionData.temperatureCelsius !== null
                ? ` · ${suggestionData.temperatureCelsius}°C`
                : ''}
              {suggestionData.text ? ` — ${suggestionData.text}` : ''}
            </AlertDescription>
            <div className="pt-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => update({ weatherCondition: suggestionData.condition })}
              >
                {t('eventNew.weatherUseSuggestion')}
              </Button>
            </div>
          </Alert>
        ) : null}

        {suggestionData && suggestionData.condition === 'unknown' ? (
          <Alert variant="warning">
            <AlertDescription>{t('eventNew.weatherUnknown')}</AlertDescription>
          </Alert>
        ) : null}

        {suggestionError ? (
          <Alert variant="warning">
            <AlertDescription>{suggestionError}</AlertDescription>
          </Alert>
        ) : null}

        <WizardFooter
          backTo="/me/events/new/activities"
          backLabel={t('common.back')}
          nextLabel={t('common.next')}
          onNext={goNext}
        />
      </CardContent>
    </Card>
  )
}
